import React, { useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { ensureOwnListing } from '../../util/data';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { ListingLink } from '..';
import { EditListingHRStrategicSkillsForm } from '../../forms';
import HRSkillsRatingModal from '../HRSkillsRatingModal/HRSkillsRatingModal';

import css from './EditListingHRStrategicSkillsPanel.module.css';

const normaliseSkills = raw => {
  if (Array.isArray(raw)) return Object.fromEntries(raw.map(k => [k, null]));
  if (raw && typeof raw === 'object') return raw;
  return {};
};

const EditListingHRStrategicSkillsPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    disabled,
    ready,
    onSubmit,
    onChange,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
    onManageDisableScrolling,
  } = props;

  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [pendingHrSkills, setPendingHrSkills] = useState({});

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  const { publicData } = currentListing?.attributes || {};
  const { hrStrategicSkills } = publicData || {};
  const isPublished = currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;
  const panelTitle = isPublished ? (
    <FormattedMessage
      id="EditListingHRStrategicSkillsPanel.title"
      values={{
        listingTitle: (
          <ListingLink listing={listing}>
            <FormattedMessage id="EditListingHRStrategicSkillsPanel.listingTitle" />
          </ListingLink>
        ),
      }}
    />
  ) : (
    <FormattedMessage id="EditListingHRStrategicSkillsPanel.createListingTitle" />
  );

  const handleFormSubmit = values => {
    const rawSkills = values.hrStrategicSkills || {};
    const selectedKeys = Object.keys(rawSkills);

    if (selectedKeys.length === 0) {
      onSubmit({ publicData: { hrStrategicSkills: {} } });
      return;
    }

    // Preserve any existing ratings already stored for skills
    const existingRatings = normaliseSkills(hrStrategicSkills);
    const skillsWithRatings = Object.fromEntries(
      selectedKeys.map(key => [key, existingRatings[key] || null])
    );
    setPendingHrSkills(skillsWithRatings);
    setIsRatingModalOpen(true);
  };

  const handleRatingConfirm = ratings => {
    setIsRatingModalOpen(false);
    onSubmit({ publicData: { hrStrategicSkills: ratings } });
  };

  const handleRatingModalClose = () => setIsRatingModalOpen(false);

  return (
    <div className={classes}>
      <h1 className={css.title}>{panelTitle}</h1>
      <EditListingHRStrategicSkillsForm
        className={css.form}
        initialValues={{ hrStrategicSkills: normaliseSkills(hrStrategicSkills) }}
        saveActionMsg={submitButtonText}
        onSubmit={handleFormSubmit}
        onChange={onChange}
        disabled={disabled}
        ready={ready}
        updated={panelUpdated}
        updateInProgress={updateInProgress}
        fetchErrors={errors}
      />
      <HRSkillsRatingModal
        isOpen={isRatingModalOpen}
        onClose={handleRatingModalClose}
        onManageDisableScrolling={onManageDisableScrolling}
        hrSkills={pendingHrSkills}
        onConfirm={handleRatingConfirm}
        updateInProgress={updateInProgress}
      />
    </div>
  );
};

EditListingHRStrategicSkillsPanel.defaultProps = {
  className: null,
  rootClassName: null,
  errors: null,
  listing: null,
};

EditListingHRStrategicSkillsPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  onChange: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
  onManageDisableScrolling: func.isRequired,
};

export default EditListingHRStrategicSkillsPanel;
